export const currentUiLocale = () => {
  if (typeof window === 'undefined') return 'en';
  try {
    return window.localStorage.getItem('talent-locale') === 'zh-CN' ? 'zh-CN' : 'en';
  } catch {
    return 'en';
  }
};

export const formatDate = (value, withTime = false, locale = currentUiLocale()) => {
  if (!value) return '—';
  const date = new Date(value);
  return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en-SG', withTime
    ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
};

export const formatMoney = (min, max, currency = 'CNY', locale = currentUiLocale()) => {
  if (min === null && max === null) return locale === 'zh-CN' ? '薪资待定' : 'Salary not specified';
  const symbol = currency === 'CNY' ? '¥' : currency === 'SGD' ? 'S$' : '$';
  const display = (value) => Number(value) >= 1000 ? `${Math.round(Number(value) / 1000)}k` : value;
  return `${symbol}${display(min)}–${display(max)}`;
};

export const scoreTone = (score) => {
  if (score === null || score === undefined) return 'neutral';
  if (score >= 86) return 'strong';
  if (score >= 78) return 'good';
  if (score >= 68) return 'review';
  return 'low';
};

export const confidenceLabel = (confidence) => {
  if (confidence >= 0.85) return '高';
  if (confidence >= 0.68) return '中';
  return '低';
};

export const statusLabel = (status) => ({
  scored: '已评分', needs_review: '需复核', processing: '处理中', queued: '已排队', ready: '待开始评估', failed: '处理失败',
  completed: '已完成', active: '招聘中', paused: '已暂停', unmatched: '待匹配', parsed: '已解析',
  link_only: '仅链接', reference_only: '仅供人工查看',
}[status] || status || '未知');

export const workflowStatusLabel = (status) => ({
  needs_review: '需复核', backup: '备选', recommended: '推荐面试', scheduling: '待约面',
  interview_scheduled: '待面试', offer_ready: '建议发 Offer', offer_sent: '已发 Offer',
  offer_accepted: '接受 Offer', offer_declined: '拒绝 Offer', hired: '已入职', not_match: '不匹配',
}[status] || '需复核');

export const workflowStatusTone = (status) => ({
  needs_review: 'warning', backup: 'neutral', recommended: 'success', scheduling: 'info',
  interview_scheduled: 'info', offer_ready: 'accent', offer_sent: 'accent', offer_accepted: 'success',
  offer_declined: 'danger', hired: 'strong', not_match: 'danger',
}[status] || 'warning');

export const formatTaskMessage = (message) => message === '评分与证据链已完成' ? '简历评分已完成' : message;

export const isDesignJobTitle = (title = '') => {
  const value = String(title).toLocaleLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!value) return false;
  return /(?:ui\s*\/?\s*ux|ux\s*\/?\s*ui|user experience|interaction design|visual design|graphic design|industrial design|product design|web design|motion design|brand design|packaging design|spatial design|interior design|creative design)/i.test(value)
    || /\b(?:ui|ux|product|industrial|interaction|visual|graphic|web|motion|brand|packaging|spatial|interior|creative)\s+designer\b/i.test(value)
    || /\b(?:design|creative)\s+(?:lead|manager|director|head)\b|\bhead\s+of\s+(?:design|creative)\b/i.test(value)
    || /(?:用户体验|交互|视觉|平面|工业|产品|品牌|包装|动效|网页|空间|室内|创意)?设计(?:师|主管|经理|总监|负责人|岗位)/i.test(value);
};

export const initials = (name = '') => {
  const words = name.trim().split(/\s+/);
  if (words.length > 1) return `${words[0][0]}${words.at(-1)[0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

export const getCandidateScore = (candidate) => {
  if (candidate.manualAdjustedScore !== null && candidate.manualAdjustedScore !== undefined) return candidate.manualAdjustedScore;
  const dimensions = candidate.dimensions || [];
  if (!dimensions.some((item) => item.key === 'portfolio')) return candidate.totalScore;
  const active = dimensions.filter((item) => item.key !== 'portfolio' && Number.isFinite(Number(item.score)));
  const totalWeight = active.reduce((sum, item) => sum + Number(item.weight || 0), 0);
  if (!active.length || !totalWeight) return candidate.totalScore;
  return Math.round(active.reduce((sum, item) => sum + Number(item.score) * Number(item.weight || 0), 0) / totalWeight);
};

export const getCandidateConfidence = (candidate) => {
  const dimensions = candidate.dimensions || [];
  if (!dimensions.some((item) => item.key === 'portfolio')) return candidate.confidence;
  const active = dimensions.filter((item) => item.key !== 'portfolio' && Number.isFinite(Number(item.confidence)));
  const totalWeight = active.reduce((sum, item) => sum + Number(item.weight || 0), 0);
  if (!active.length || !totalWeight) return candidate.confidence;
  return active.reduce((sum, item) => sum + Number(item.confidence) * Number(item.weight || 0), 0) / totalWeight;
};

export const getCandidateRecommendation = (candidate) => {
  return (candidate.dimensions || []).some((item) => item.key === 'portfolio') ? '继续复核' : candidate.recommendation;
};

export const classNames = (...values) => values.filter(Boolean).join(' ');

function structuredContactText(asset) {
  const sections = asset.extraction?.documentStructure?.sections || [];
  return sections
    .filter((section) => section.canonical === 'contact' || /contact|联系方式|个人信息/i.test(section.title || ''))
    .flatMap((section) => section.blocks || [])
    .flatMap((block) => [block.title, ...(block.meta || []), block.label, block.text])
    .filter(Boolean)
    .join('\n');
}

function resumeEvidenceText(candidate) {
  return (candidate.assets || [])
    .filter((asset) => asset.kind?.startsWith('resume'))
    .map((asset) => [
      structuredContactText(asset),
      asset.extraction?.text,
      asset.extraction?.ocrText,
      ...(asset.extraction?.visionOcr || []).map((page) => page.text),
    ].filter(Boolean).join('\n'))
    .join('\n');
}

function detectedPhone(text) {
  const candidates = String(text).match(/(?:\+?\d[\d \t().-]{6,}\d)/g) || [];
  return candidates.map((value) => value.trim()).find((value) => {
    const digits = value.replace(/\D/g, '');
    const years = value.match(/(?:19|20)\d{2}/g) || [];
    if (digits.length < 8 || digits.length > 15 || years.length >= 2) return false;
    if (/^(?:19|20)\d{2}[./-]\d{1,2}/.test(value)) return false;
    return value.startsWith('+') || digits.length >= 10 || /^[3689]\d{3}[ -]?\d{4}$/.test(value);
  }) || '';
}

export function getResumeContact(candidate) {
  const profileContact = candidate.resumeProfile?.contact || {};
  const evidenceText = resumeEvidenceText(candidate);
  const detectedEmail = (evidenceText.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i) || [])[0] || '';
  const email = String(profileContact.email || detectedEmail).trim();
  const phone = String(profileContact.phone || detectedPhone(evidenceText)).trim();
  return {
    email,
    phone,
    evidence: String(profileContact.evidence || [email, phone].filter(Boolean).join(' · ')).trim(),
  };
}

const LANGUAGE_NAMES = [
  ['English', '英语'], ['Mandarin', '普通话'], ['Chinese', '中文'], ['Tamil', '泰米尔语'],
  ['Hindi', '印地语'], ['Vietnamese', '越南语'], ['Malay', '马来语'], ['Indonesian', '印尼语'],
  ['Thai', '泰语'], ['Filipino', '菲律宾语'], ['Tagalog', '他加禄语'], ['Japanese', '日语'],
  ['Korean', '韩语'], ['French', '法语'], ['German', '德语'], ['Spanish', '西班牙语'],
  ['Arabic', '阿拉伯语'], ['Cantonese', '粤语'], ['英语', '英语'], ['中文', '中文'],
  ['普通话', '普通话'], ['粤语', '粤语'], ['日语', '日语'], ['韩语', '韩语'],
];

function languageEvidenceText(candidate) {
  const text = resumeEvidenceText(candidate);
  const sections = [];
  const pattern = /(?:^|\n)\s*(?:languages?|语言能力|语言)\s*[:：]?\s*([\s\S]{0,220})/gi;
  for (const match of text.matchAll(pattern)) sections.push(match[1].split(/\n\s*\n|\n(?=[A-Z][A-Z &/]{4,}\s*$)/m)[0]);
  return sections.join('\n');
}

export function getResumeLanguages(candidate) {
  const explicit = candidate.resumeProfile?.languages || [];
  if (explicit.length) return explicit;
  const evidence = languageEvidenceText(candidate);
  if (!evidence) return [];
  const found = [];
  const seen = new Set();
  for (const [source, label] of LANGUAGE_NAMES) {
    const escaped = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = evidence.match(new RegExp(`(?:^|[,;、\\n\\s])(${escaped})(?:\\s*[（(]([^）)]+)[）)])?`, 'i'));
    if (!match || seen.has(label)) continue;
    seen.add(label);
    found.push({ language: label, proficiency: String(match[2] || '').trim(), evidence: match[0].trim() });
  }
  return found;
}

const REGION_MARKERS = [
  ['Singapore', '新加坡'], ['Vietnam', '越南'], ['Bangalore', '印度（班加罗尔）'],
  ['Coimbatore', '印度（哥印拜陀）'], ['India', '印度'], ['Dubai', '阿联酋（迪拜）'],
  ['United Arab Emirates', '阿联酋'], ['Malaysia', '马来西亚'], ['Philippines', '菲律宾'],
  ['Indonesia', '印度尼西亚'], ['Thailand', '泰国'], ['China', '中国'], ['Hong Kong', '中国香港'],
  ['United States', '美国'], ['USA', '美国'], ['United Kingdom', '英国'], ['Australia', '澳大利亚'],
];

export function getResumeRegions(candidate) {
  const text = resumeEvidenceText(candidate);
  const found = [];
  const seen = new Set();
  for (const [marker, label] of REGION_MARKERS) {
    const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = text.match(new RegExp(`(?:^|[^A-Za-z])(${escaped})(?=$|[^A-Za-z])`, 'i'));
    if (!match || seen.has(label)) continue;
    const index = (match.index || 0) + match[0].indexOf(match[1]);
    seen.add(label);
    found.push({ label, evidence: text.slice(Math.max(0, index - 45), index + marker.length + 45).replace(/\s+/g, ' ').trim() });
  }
  return found.slice(0, 6);
}

export function getResumeCareerSpan(candidate) {
  const text = resumeEvidenceText(candidate);
  const start = text.search(/work experience|professional experience|工作经历|职业经历/i);
  if (start < 0) return null;
  const section = text.slice(start, start + 12000).split(/\n\s*(?:education|教育背景|学历)\s*\n/i)[0];
  const currentYear = new Date().getFullYear();
  const years = [...section.matchAll(/\b((?:19|20)\d{2})\b\s*(?:[-–—=]|to)\s*(?:(?:[A-Za-z]{3,9}\s+)?(?:19|20)\d{2}|present|至今)/gi)]
    .map((match) => Number(match[1])).filter((year) => year >= 1960 && year <= currentYear);
  if (!years.length) return null;
  const startYear = Math.min(...years);
  return { startYear, years: Math.max(0, currentYear - startYear), evidence: `最早明确工作经历年份：${startYear}` };
}

export const assetKindLabel = (kind) => ({
  resume: '简历', resume_image: '图片简历', portfolio: '作品集',
  portfolio_image: '图片作品集', portfolio_web: '网页作品集',
}[kind] || kind);

export const fileTypeFromName = (name) => {
  if (/作品集|portfolio/i.test(name)) return '作品集';
  if (/\.(png|jpe?g|webp|tiff?)$/i.test(name)) return '图片简历';
  return '简历';
};

export const candidateNameFromFile = (name) => name
  .replace(/\.[^.]+$/, '')
  .replace(/【[^】]+】/g, '')
  .replace(/\[[^\]]+\]/g, '')
  .replace(/\s+(?:(?:e-?commerce|dtc|amazon|shopify|brand|digital|social\s+media|performance|ui\s*\/?\s*ux|product|industrial)\s+)*(?:executive|specialist|manager|director|lead|designer|operator|marketer)\b.*$/i, ' ')
  .replace(/[（(][^）)]*(resume|portfolio|简历|作品集|ui\s*\/?\s*ux|designer|design|e-?commerce|website)[^）)]*[）)]/gi, ' ')
  .replace(/^(作品集|portfolio|resume|cv)[-_\s]*/i, '')
  .replace(/(作品集|portfolio|resume|简历)/gi, '')
  .replace(/[_-]?(一年以内|\d+年|\d+\s*years?)$/i, '')
  .replace(/\b(ui\s*\/?\s*ux|designer|design|e-?commerce|website)\b/gi, ' ')
  .replace(/[（(]\s*[）)]/g, ' ')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();
